import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import doorsImg from 'assets/doors.jpeg';
import windowsImg from 'assets/windows.jpeg';
import stairsImg from 'assets/stairs.jpeg';
import burglarProofImg from 'assets/burglarproof.jpeg';
import pagolasImg from 'assets/pagolas.png';
import tangstandsImg from 'assets/tangstands.png';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.08 * i, duration: 0.45, ease: 'easeOut' },
  }),
};

const services = [
  {
    key: 'windows-doors',
    title: 'Windows & Doors',
    desc: 'Secure and stylish openings designed for airflow, light, and safety.',
    bullets: ['Steel & aluminum options', 'Custom sizes & patterns', 'Weather-sealed & durable'],
    img: doorsImg,
  },
  {
    key: 'burglarproofing',
    title: 'Burglarproofing',
    desc: 'Protective grills and guards that complement your décor.',
    bullets: ['Decorative patterns', 'Child-safe spacing', 'Rust-resistant finishes'],
    img: burglarProofImg,
  },
  {
    key: 'stairs-railings',
    title: 'Stairs & Railings',
    desc: 'Durable staircases and decorative rails for indoor and outdoor spaces.',
    bullets: ['Balconies & verandas', 'Handrails & guardrails', 'Anti-slip treads'],
    img: stairsImg,
  },
  {
    key: 'pagolas-balconies',
    title: 'Pagolas & Balconies',
    desc: 'Outdoor shade structures and balcony systems for comfort and style.',
    bullets: ['Site-measured for fit', 'Weather-tough fabrication', 'Modern & classic designs'],
    img: pagolasImg,
  },
  {
    key: 'fittings',
    title: 'Door & Window Fittings',
    desc: 'Precision hardware for smooth, reliable operation.',
    bullets: ['Hinges, locks, rollers', 'Handle sets & closers', 'Replacement & upgrades'],
    img: windowsImg,
  },
  {
    key: 'tangstands',
    title: 'Tangstands',
    desc: 'Robust stands and supports tailored to your application.',
    bullets: ['Heavy-duty construction', 'Custom dimensions', 'Field-ready durability'],
    img: tangstandsImg,
  },
];

export function Services() {
  return (
    <WebLayout>
      {/* Header */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-3xl">
            <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-gray-300">
              Our Services
            </span>
            <h1 className="mt-4 text-4xl font-extrabold text-white md:text-5xl">
              Custom Metal Works, Built for Life
            </h1>
            <p className="mt-4 text-gray-300 md:text-lg">
              From windows and doors to balconies and precision fittings, we fabricate, finish, and
              install metalwork that elevates your space—crafted for strength, style, and security.
            </p>
            <div className="mt-6">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-green-500 px-5 py-3 font-semibold text-black shadow hover:brightness-105"
              >
                Get a Free Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Cards */}
      <section className="bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <motion.article
                key={s.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeUp}
                custom={i}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950"
              >
                {s.img && (
                  <div className="relative h-44 w-full overflow-hidden">
                    <img
                      src={s.img}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-1 text-sm text-gray-400">{s.desc}</p>
                  <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-300">
                    {s.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                  <div className="mt-4">
                    <Link
                      to="/contact"
                      className="text-sm font-semibold text-green-400 underline-offset-4 hover:underline"
                    >
                      Request a site visit
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Material / Finish Note */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={services.length + 1}
            className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-gray-300"
          >
            <p>
              <span className="font-semibold text-white">Materials & finishes:</span> Steel, wrought
              iron, and aluminum available. Finishing options include powder coat, paint systems,
              and galvanizing for long-term durability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-6 py-14 md:py-18">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-2xl font-bold text-white md:text-3xl"
          >
            How We Work
          </motion.h2>
          <p className="mt-2 max-w-2xl text-gray-400">
            A clear, efficient workflow so your project moves smoothly from idea to installation.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-4">
            {[
              {
                n: '01',
                t: 'Consult',
                d: 'We discuss goals, style, and budget; we review photos or do a site visit.',
              },
              { n: '02', t: 'Design', d: 'Measurements and drawings shared for your approval.' },
              {
                n: '03',
                t: 'Fabricate',
                d: 'Precision welding, fit-up, and finishing in our workshop.',
              },
              {
                n: '04',
                t: 'Install',
                d: 'Clean, safe installation and handover, with care instructions.',
              },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
                className="rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 p-5"
              >
                <div className="text-sm text-white/60">{s.n}</div>
                <div className="mt-1 text-lg font-semibold text-white">{s.t}</div>
                <div className="mt-1 text-sm text-gray-400">{s.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-green-500 to-yellow-500">
        <div
          className="absolute inset-0 opacity-10 mix-blend-multiply"
          style={{ backgroundImage: "url('/assets/pattern-noise.png')" }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-12 md:py-16">
          <div className="grid items-center gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-extrabold text-black md:text-3xl">
                Ready to start your metalwork project?
              </h3>
              <p className="mt-1 text-black/80">
                Send measurements or book a site visit—we’ll prepare a free, fast quote.
              </p>
            </div>
            <div className="flex gap-3 md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg bg-black px-5 py-3 font-semibold text-white hover:opacity-90"
              >
                Contact Us
              </Link>
              <a
                href="https://wa.me/256779706977"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-lg border border-black/20 bg-white/90 px-5 py-3 font-semibold text-black hover:bg-white"
              >
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
